/* The phone screen a client answers their article questions on.

   Reached from a link in an email, with no login — the token in the URL is the
   credential (see worker/answer.ts). It renders OUTSIDE AppShell on purpose:
   no sidebar, no topbar, no nav. Someone who tapped a link in their inbox is
   here to answer five questions and leave, and dashboard chrome on a 390px
   screen is all of it in the way.

   One question per screen rather than a scrolling form. A list of five open
   textareas reads as homework; one question with a big box reads as a
   conversation, and it keeps the keyboard from hiding the question you are
   answering. */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Icon } from '@/lib/icons';
import { useDictation } from '@/lib/dictation';

interface Question { id: string; question: string; answer: string; status: string; draft: string; draftSource: string }
interface Payload { topic: string; business: string; answeringAs: string; closed: boolean; questions: Question[] }

/* Plain fetch, not apiFetch: that helper attaches a Clerk bearer token, and the
   whole point of this screen is that there is no session to attach. */
const api = (path: string, init?: RequestInit) =>
  fetch(`/api/answer${path}`, { ...init, headers: { 'content-type': 'application/json', ...(init?.headers || {}) } });

export function AnswerPage() {
  const { token = '' } = useParams();
  const [data, setData] = useState<Payload | null>(null);
  const [error, setError] = useState('');
  const [at, setAt] = useState(0);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(0);
  const [done, setDone] = useState(false);
  const [sending, setSending] = useState(false);
  const taRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    api(`/${token}`)
      .then(async (r) => {
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error((j as { error?: string }).error || 'That link is no longer valid.');
        const p = j as Payload;
        setData(p);
        setDone(p.closed);
        // Open on the first unanswered question — returning to finish should not
        // mean swiping past the ones already done.
        const first = p.questions.findIndex((q) => q.status === 'pending');
        const idx = first === -1 ? 0 : first;
        setAt(idx);
        setText(p.questions[idx]?.answer || '');
      })
      .catch((e: Error) => setError(e.message));
  }, [token]);

  const q = data?.questions[at];

  const save = useCallback(async (value: string) => {
    if (!q) return;
    setSaving(true);
    try {
      const r = await api(`/${token}/${q.id}`, { method: 'PUT', body: JSON.stringify({ answer: value }) });
      if (r.ok) {
        setSavedAt(Date.now());
        setData((d) => d && ({
          ...d,
          questions: d.questions.map((x) => x.id === q.id
            ? { ...x, answer: value, status: value.trim() ? 'answered' : 'pending' } : x),
        }));
      }
    } finally { setSaving(false); }
  }, [q, token]);

  /* Dictation appends into the same box. The mic is hidden where the Web Speech
     API does not exist — notably every browser on iOS — because showing a dead
     button is worse than showing none: iPhone users have a mic on their own
     keyboard, which works in this textarea already. */
  // auth: false — this page is reached by a token, with no session to ask /api/me for an engine.
  const { listening, start, stop, micError, supported: voiceable } = useDictation((next) => { setText(next); }, { auth: false });
  // start() takes what is already in the box so dictation appends rather than
  // replacing a half-typed answer.
  const toggleMic = () => (listening ? stop() : start(text));

  if (error) {
    return (
      <div className="ans">
        <div className="ans__card">
          <Icon name="alert-circle" size={28} />
          <h1>Link expired</h1>
          <p>{error}</p>
        </div>
      </div>
    );
  }
  if (!data) return <div className="ans"><div className="ans__card"><p>Loading your questions…</p></div></div>;

  if (done) {
    return (
      <div className="ans">
        <div className="ans__card">
          <div className="ans__tick"><Icon name="check-circle" size={34} /></div>
          <h1>Thank you</h1>
          <p>Your answers are with the writing team. We'll use them to write <strong>{data.topic}</strong> — nothing else is needed from you.</p>
        </div>
      </div>
    );
  }

  const answered = data.questions.filter((x) => x.status === 'answered' && x.answer.trim()).length;
  const last = at >= data.questions.length - 1;

  const go = async (delta: number) => {
    await save(text);
    const next = Math.min(data.questions.length - 1, Math.max(0, at + delta));
    setAt(next);
    setText(data.questions[next]?.answer || '');
    taRef.current?.focus();
  };

  const skip = async () => {
    if (!q) return;
    await api(`/${token}/${q.id}/skip`, { method: 'POST', body: JSON.stringify({}) });
    setData((d) => d && ({ ...d, questions: d.questions.map((x) => x.id === q.id ? { ...x, status: 'declined', answer: '' } : x) }));
    if (!last) await go(1); else setText('');
  };

  const finish = async () => {
    setSending(true);
    await save(text);
    const r = await api(`/${token}/done`, { method: 'POST', body: JSON.stringify({}) });
    setSending(false);
    if (r.ok) setDone(true);
    else {
      const j = await r.json().catch(() => ({}));
      setError((j as { error?: string }).error || 'Could not send your answers.');
    }
  };

  return (
    <div className="ans">
      <header className="ans__head">
        <p className="ans__biz">{data.business}</p>
        <h1>{data.topic}</h1>
        {data.answeringAs && <p className="ans__as">Answering as {data.answeringAs}</p>}
      </header>

      <div className="ans__dots" role="progressbar" aria-valuenow={at + 1} aria-valuemin={1} aria-valuemax={data.questions.length}>
        {data.questions.map((x, i) => (
          <span key={x.id}
            className={`ans__dot ${i === at ? 'is-at' : ''} ${x.status === 'answered' && x.answer.trim() ? 'is-done' : ''} ${x.status === 'declined' ? 'is-skipped' : ''}`} />
        ))}
      </div>

      <main className="ans__body">
        <p className="ans__count">Question {at + 1} of {data.questions.length}</p>
        <h2 className="ans__q">{q?.question}</h2>

        {/* Shown as a draft beside its source, with the answer box still
            empty: the client is confirming something we found, not reading
            back their own words. Naming the source is what makes "does this
            sound right?" answerable at all. */}
        {q?.draft && !text.trim() && (
          <div className="ans__draft">
            <p className="ans__draft__h">You told us this before — still right?</p>
            <p className="ans__draft__a">{q.draft}</p>
            {q.draftSource && <p className="ans__draft__src">from {q.draftSource}</p>}
            <div className="ans__draft__acts">
              <button type="button" className="ans__btn ans__btn--go" onClick={() => { setText(q.draft); void save(q.draft); }}>
                Yes, use this
              </button>
              <button type="button" className="ans__btn" onClick={() => { setText(q.draft); taRef.current?.focus(); }}>
                Edit it
              </button>
            </div>
          </div>
        )}

        <div className="ans__field">
          <textarea
            ref={taRef}
            className="ans__input"
            value={text}
            placeholder="Type your answer…"
            onChange={(e) => setText(e.target.value)}
            onBlur={() => save(text)}
            rows={7}
          />
          {voiceable && (
            <button type="button" className={`ans__mic ${listening ? 'is-on' : ''}`} onClick={toggleMic}
              aria-label={listening ? 'Stop dictation' : 'Dictate your answer'}>
              <Icon name="mic" size={18} />
            </button>
          )}
        </div>
        {micError && <p className="ans__err">{micError}</p>}
        <p className="ans__save">
          {saving ? 'Saving…' : savedAt ? 'Saved' : 'Answer in your own words — a sentence or two is plenty.'}
        </p>

        <button type="button" className="ans__skip" onClick={skip}>I don't know this one — skip it</button>
      </main>

      <footer className="ans__foot">
        <button type="button" className="ans__btn" disabled={at === 0} onClick={() => go(-1)}>Back</button>
        {last
          ? <button type="button" className="ans__btn ans__btn--go" disabled={sending || !answered} onClick={finish}>
              {sending ? 'Sending…' : 'Send my answers'}
            </button>
          : <button type="button" className="ans__btn ans__btn--go" onClick={() => go(1)}>Next</button>}
      </footer>

      {/* Always available, not just on the last question: a client who can answer
          two of five should be able to hand those over without swiping to the end. */}
      {!last && answered > 0 && (
        <button type="button" className="ans__send" disabled={sending} onClick={finish}>
          {sending ? 'Sending…' : `Send the ${answered} I've answered`}
        </button>
      )}
    </div>
  );
}
